import { Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { auth } from '@/lib/auth';
import { useIsDemo, useUpdateMe } from '@/lib/clients';
import { setLanguage, useLanguage } from '@/lib/i18n';

/**
 * English / Arabic switch on every page (ADR-0026). It names the other language in its own script,
 * as bilingual sites do ("العربية" in English, "English" in Arabic). Signed in, the choice is saved
 * to the profile so it follows the user; signed out (public pages), this browser remembers it.
 */
export function LanguageSwitch({
  signedIn = false,
  className,
}: {
  signedIn?: boolean;
  className?: string;
}) {
  const lang = useLanguage();
  const next = lang === 'ar' ? 'en' : 'ar';
  const label = next === 'ar' ? 'العربية' : 'English';
  return signedIn ? (
    <SignedInSwitch next={next} label={label} className={className} />
  ) : (
    <SwitchButton
      label={label}
      lang={next}
      className={className}
      onClick={() => void setLanguage(next)}
    />
  );
}

function SignedInSwitch({
  next,
  label,
  className,
}: {
  next: 'en' | 'ar';
  label: string;
  className?: string;
}) {
  const update = useUpdateMe();
  const isDemo = useIsDemo();
  return (
    <SwitchButton
      label={label}
      lang={next}
      className={className}
      onClick={() => {
        void setLanguage(next);
        // the demo users are shared, so their language stays in this browser only
        if (auth.enabled && !isDemo) update.mutate({ language: next });
      }}
    />
  );
}

function SwitchButton({
  label,
  lang,
  className,
  onClick,
}: {
  label: string;
  lang: 'en' | 'ar';
  className?: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      lang={lang}
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      onClick={onClick}
      className={className}
      data-testid="language-switch"
    >
      <Languages className="size-4" aria-hidden />
      <span>{label}</span>
    </Button>
  );
}
